import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';

// Sanitize a URL-provided string: strip anything that isn't a letter, digit,
// space, hyphen, apostrophe, or ampersand; clamp length.
function sanitize(raw, max = 40) {
  if (!raw) return '';
  return String(raw).replace(/[^a-zA-Z0-9\s\-'&]/g, '').trim().slice(0, max);
}

/**
 * Shows a sticky banner at the top of the app when someone lands via a
 * personalized share link (?from=... &to=...).
 *
 * Reads params once on mount. Dismissing it stores a flag so it doesn't
 * re-show during the same session.
 */
export default function PersonalShareBanner() {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const from = sanitize(params.get('from'), 30);
      const to = sanitize(params.get('to'), 30);
      if (!from && !to) return;

      // Don't re-show in the same session once dismissed
      if (sessionStorage.getItem('pshare_dismissed') === '1') return;

      let text;
      if (from && to) {
        text = `${from} thinks their ${to} is being an idiot about EVs. Here's why.`;
      } else if (from) {
        text = `${from} thinks you're being an idiot about EVs. Here's why.`;
      } else {
        text = `Someone thinks their ${to} is being an idiot about EVs. Here's why.`;
      }

      setMessage(text);
      setVisible(true);

      if (window.gtag) {
        window.gtag('event', 'personal_share_landing', {
          has_from: !!from,
          has_to: !!to,
        });
      }
    } catch (_) {
      // URL parsing fallthrough -- just don't show the banner
    }
  }, []);

  const dismiss = () => {
    if (window.gtag) window.gtag('event', 'personal_share_dismiss', {});
    setVisible(false);
    try { sessionStorage.setItem('pshare_dismissed', '1'); } catch (_) { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <Box
      role="status"
      data-personal-share-banner
      sx={{
        background: 'linear-gradient(90deg, #D7140F 0%, #ef5350 100%)',
        color: '#fff',
        position: 'relative',
        zIndex: 1200,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        padding: { xs: '10px 40px 10px 14px', md: '12px 48px' },
        textAlign: 'center',
        fontWeight: 600,
      }}
    >
      <LocalFireDepartmentIcon sx={{ fontSize: '20px', flexShrink: 0 }} />
      <Typography sx={{ fontSize: { xs: '13px', md: '15px' }, fontWeight: 600, lineHeight: 1.4 }}>
        {message}
      </Typography>
      <IconButton
        aria-label="Dismiss"
        onClick={dismiss}
        sx={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'rgba(255,255,255,0.85)',
          '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
        }}
        size="small"
      >
        <CloseIcon sx={{ fontSize: '18px' }} />
      </IconButton>
    </Box>
  );
}
